import React, { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Loader2, Newspaper } from 'lucide-react'
import newsService, {
  type NewsFeedItem,
  type NewsScope,
  type NewsFeedResponse,
  filterNewsFeedByScope,
  isNewsMatchCard,
  isNewsTournamentExtraCard,
  isNewsRaceCard,
} from '../services/newsService'
import NewsPredictionCard from '../components/news/NewsPredictionCard'
import NewsTournamentExtraCard from '../components/news/NewsTournamentExtraCard'
import NewsRaceCard from '../components/news/NewsRaceCard'
import { useTranslation } from '../hooks/use-i18n'

const PAGE_SIZE = 5

/** Soon + past windows — visually highlighted vs sport / Winners. */
const TIME_SCOPES: NewsScope[] = ['upcoming', 'past_week', 'past_month', 'past_3months']
const OTHER_SCOPES: NewsScope[] = ['winners']

function emptyKeyForScope(scope: NewsScope): string {
  if (scope === 'upcoming' || scope === 'winners') return scope
  return 'past'
}

const News: React.FC = () => {
  const { t } = useTranslation()
  const [scope, setScope] = useState<NewsScope>('upcoming')
  const [sport, setSport] = useState<'football' | 'formula1'>('football')

  const query = useInfiniteQuery({
    queryKey: ['news', sport, scope],
    queryFn: ({ pageParam = 0 }) =>
      sport === 'formula1'
        ? newsService.getFormula1(scope, PAGE_SIZE, pageParam as number)
        : newsService.getFootball(scope, PAGE_SIZE, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (last: NewsFeedResponse) =>
      last.hasMore ? last.offset + last.limit : undefined,
    staleTime: 60 * 1000,
  })

  const items: NewsFeedItem[] = filterNewsFeedByScope(
    query.data?.pages.flatMap((p) => p.items) ?? [],
    scope,
  )
  const isInitialLoading = query.isLoading

  return (
    <div className="min-h-screen">
      <main className="container max-w-2xl mx-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Newspaper className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t('news.title')}</h1>
        </div>
        <p className="text-sm text-foreground/70 mb-4">
          {sport === 'formula1' ? t('news.subtitle.formula1') : t('news.subtitle')}
        </p>

        <div className="flex flex-wrap gap-2 mb-3">
          {(['football', 'formula1'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSport(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium inline-flex items-center gap-1.5 ${
                sport === s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/30 text-foreground hover:bg-muted/50'
              }`}
            >
              <span aria-hidden>{s === 'formula1' ? '🏎️' : '⚽'}</span>
              {s === 'formula1' ? t('news.sport.formula1') : t('news.sport.football')}
            </button>
          ))}
        </div>

        <div className="mb-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-2.5 shadow-[0_0_24px_rgba(251,191,36,0.12)]">
          <div className="flex flex-wrap gap-2">
            {TIME_SCOPES.map((s) => {
              const active = scope === s
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold tracking-wide transition-colors ${
                    active
                      ? 'bg-amber-400 text-slate-950 shadow-md shadow-amber-400/30 ring-2 ring-amber-200/80'
                      : 'bg-slate-950/40 text-amber-100 border border-amber-400/35 hover:bg-amber-400/15 hover:border-amber-300/60'
                  }`}
                >
                  {t(`news.tab.${s}`)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {OTHER_SCOPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                scope === s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/30 text-foreground hover:bg-muted/50'
              }`}
            >
              {s === 'winners' && sport === 'formula1' ? t('news.tab.winnersF1') : t(`news.tab.${s}`)}
            </button>
          ))}
        </div>

        {isInitialLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {!isInitialLoading && items.length === 0 && (
          <p className="text-foreground/70 py-12 text-center">
            {sport === 'formula1'
              ? t(`news.empty.formula1.${emptyKeyForScope(scope)}`)
              : t(`news.empty.${emptyKeyForScope(scope)}`)}
          </p>
        )}

        <div className="space-y-5">
          {items.map((item) =>
            isNewsTournamentExtraCard(item) ? (
              <NewsTournamentExtraCard key={item.eventId} card={item} />
            ) : isNewsRaceCard(item) ? (
              <NewsRaceCard key={item.raceId} card={item} />
            ) : isNewsMatchCard(item) ? (
              <NewsPredictionCard key={item.matchId} card={item} />
            ) : null,
          )}
        </div>

        {query.hasNextPage && (
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="w-full mt-6 py-2.5 rounded-xl border border-border hover:bg-muted/30 text-sm font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {query.isFetchingNextPage && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('news.loadMore')}
          </button>
        )}
      </main>
    </div>
  )
}

export default News
